import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '@kss/api';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { RefreshCw, ChefHat, ChevronDown, ChevronRight, UtensilsCrossed, Clock } from 'lucide-react';

/**
 * Kitchen Order Ticket (KOT) board — replaces the generic API-browser grid,
 * which dumped each ticket's metadata into an unreadable row.
 *
 * KOTs are grouped TABLE-WISE. Each card shows the table's tickets; clicking
 * it expands every KOT with its number, station, time, status (an admin can
 * change) and the itemised lines (read from the KotItem table by kotId).
 */

const POLL_MS = 15_000;
const KOT_STATUSES = ['Issued', 'Sent', 'Acknowledged', 'Preparing', 'Ready', 'Served', 'Cancelled'] as const;
// Valid RestaurantOrder.orderStatus values — these are constrained at the DB
// level (CK_RestaurantOrder_OrderStatus). A synthetic "order" ticket writes one
// of THESE, never the KOT vocabulary above (which 500s the RestaurantOrder PUT).
const ORDER_TICKET_STATUSES = ['Placed', 'Accepted', 'Cooking', 'Ready', 'Served', 'Billed', 'Cancelled'] as const;
// Once the kitchen marks a ticket Ready/Served it's done cooking → it leaves the
// table lanes and surfaces in the "Ready to Bill" lane so staff know to bill it.
const READY_TO_BILL = new Set(['Ready', 'Served']);

const pickList = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};

function kotColor(s: string): { bg: string; fg: string } {
  switch (s) {
    case 'Issued': case 'Sent': return { bg: '#dbeafe', fg: '#1d4ed8' };
    case 'Acknowledged': return { bg: '#ede9fe', fg: '#7c3aed' };
    case 'Preparing': return { bg: '#fde68a', fg: '#9a3412' };
    case 'Ready': return { bg: '#bbf7d0', fg: '#166534' };
    case 'Served': return { bg: '#dcfce7', fg: '#166534' };
    case 'Cancelled': return { bg: '#fee2e2', fg: '#991b1b' };
    default: return { bg: '#e4e4e7', fg: '#3f3f46' };
  }
}

interface KotItem { name: string; quantity: number; note?: string; status?: string }
interface Kot {
  id: string;
  number: string;
  station?: string;
  status: string;
  tableId?: string;
  tableLabel: string;
  issuedAt?: string;
  orderId?: string;
  items: KotItem[];
  /** True when this ticket is derived from a RestaurantOrder with no KOT row
   *  yet (so a status change writes to RestaurantOrder, not Kot). */
  isOrder?: boolean;
}
interface TableGroup { key: string; label: string; kots: Kot[]; itemCount: number }

export default function KotBoard() {
  const toast = useToast();
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [orders, setOrders] = useState<Array<Record<string, unknown>>>([]);
  const [tableMap, setTableMap] = useState<Map<string, string>>(new Map());
  const [itemMap, setItemMap] = useState<Map<string, KotItem[]>>(new Map());
  const [orderLineMap, setOrderLineMap] = useState<Map<string, KotItem[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [kotRes, tableRes, itemRes, orderRes, orderLineRes] = await Promise.all([
        apiClient.get('/restaurant/Kot'),
        apiClient.get('/restaurant/RestaurantTable').catch(() => ({ data: [] })),
        apiClient.get('/restaurant/KotItem').catch(() => ({ data: [] })),
        apiClient.get('/restaurant/RestaurantOrder').catch(() => ({ data: [] })),
        apiClient.get('/restaurant/RestaurantOrderLine').catch(() => ({ data: [] })),
      ]);
      const tMap = new Map<string, string>();
      for (const t of pickList(tableRes.data)) {
        const id = String(t.id ?? (t as { restaurantTableId?: unknown }).restaurantTableId ?? '');
        if (id) tMap.set(id, String(t.name ?? t.code ?? `Table ${id}`));
      }
      setTableMap(tMap);
      // KOT items keyed by kotId (the separate KotItem table).
      const iMap = new Map<string, KotItem[]>();
      for (const it of pickList(itemRes.data)) {
        const kid = String(it.kotId ?? '');
        if (!kid || kid === '0') continue;
        const arr = iMap.get(kid) ?? iMap.set(kid, []).get(kid)!;
        arr.push({ name: String(it.itemName ?? it.name ?? '—'), quantity: Number(it.quantity ?? 1) || 1, note: it.modifierNote ? String(it.modifierNote) : undefined, status: it.itemStatus ? String(it.itemStatus) : undefined });
      }
      setItemMap(iMap);
      // Order items keyed by restaurantOrderId — the KOT fallback. Prefer the
      // separate RestaurantOrderLine rows; else recover from the order's
      // `description` JSON (floor orders stash their items there).
      const olMap = new Map<string, KotItem[]>();
      for (const l of pickList(orderLineRes.data)) {
        const oid = String(l.restaurantOrderId ?? '');
        if (!oid || oid === '0') continue;
        const arr = olMap.get(oid) ?? olMap.set(oid, []).get(oid)!;
        arr.push({ name: String(l.itemName ?? l.name ?? '—'), quantity: Number(l.quantity ?? 1) || 1, note: l.note ? String(l.note) : undefined });
      }
      const orderList = pickList(orderRes.data);
      for (const o of orderList) {
        const oid = String(o.id ?? '');
        if (!oid || olMap.has(oid)) continue;
        try {
          const env = JSON.parse(String(o.description ?? '{}')) as { items?: Array<Record<string, unknown>>; lines?: Array<Record<string, unknown>> };
          const src = (Array.isArray(env.items) && env.items.length ? env.items : (Array.isArray(env.lines) ? env.lines : []));
          if (src.length) olMap.set(oid, src.map((it) => ({ name: String(it.name ?? it.itemName ?? '—'), quantity: Number(it.quantity ?? it.qty ?? 1) || 1, note: it.note ? String(it.note) : undefined })));
        } catch { /* no description envelope */ }
      }
      setOrderLineMap(olMap);
      setOrders(orderList);
      setRows(pickList(kotRes.data));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load KOTs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(t);
  }, [refresh]);

  const kots = useMemo<Kot[]>(() => {
    const orderById = new Map(orders.map((o) => [String(o.id ?? ''), o]));
    const list: Kot[] = [];
    const ordersWithKot = new Set<string>();

    for (const r of rows) {
      // Drop the auto-seeded placeholder row (no order, no table, sample text).
      if (String(r.code ?? '').startsWith('SAMPLE-') && r.restaurantOrderId == null) continue;
      const orderId = r.restaurantOrderId != null && String(r.restaurantOrderId) !== '0' ? String(r.restaurantOrderId) : undefined;
      if (orderId) ordersWithKot.add(orderId);
      const ord = orderId ? orderById.get(orderId) : undefined;
      // Resolve the table from the KOT, falling back to its linked order.
      const ordTableId = ord?.tableId != null && String(ord.tableId) !== '0' ? String(ord.tableId) : undefined;
      const tableId = (r.tableId != null && String(r.tableId) !== '0' ? String(r.tableId) : undefined) ?? ordTableId;
      // Items: KotItem rows → linked order's lines/description → the KOT row's
      // OWN description envelope (sendToKot stashes a recovery copy there for
      // when the KotItem POSTs fail, so the kitchen still sees what to cook).
      const fromKotDesc = (): KotItem[] => {
        try {
          const env = JSON.parse(String(r.description ?? '{}')) as { items?: Array<Record<string, unknown>>; lines?: Array<Record<string, unknown>> };
          const src = (Array.isArray(env.items) && env.items.length ? env.items : (Array.isArray(env.lines) ? env.lines : []));
          return src.map((it) => ({ name: String(it.name ?? it.itemName ?? '—'), quantity: Number(it.quantity ?? it.qty ?? 1) || 1, note: it.note ? String(it.note) : undefined }));
        } catch { return []; }
      };
      const items = itemMap.get(String(r.id ?? '')) ?? (orderId ? orderLineMap.get(orderId) : undefined) ?? fromKotDesc();
      list.push({
        id: String(r.id ?? ''),
        number: String(r.kotNumber || r.code || `#${r.id ?? ''}`),
        station: r.station ? String(r.station) : undefined,
        status: String(r.kotStatus ?? 'Issued'),
        tableId,
        tableLabel: (tableId && tableMap.get(tableId)) || (tableId ? `Table ${tableId}` : 'Unassigned'),
        issuedAt: r.issuedAt && String(r.issuedAt) !== '0001-01-01T00:00:00' ? String(r.issuedAt) : (r.indate ? String(r.indate) : undefined),
        orderId,
        items,
      });
    }

    // Surface RestaurantOrders that have items but no KOT yet, so the kitchen
    // still sees what to cook (dine-in orders don't always create a KOT).
    const HIDE = new Set(['cancelled', 'paid', 'closed', 'completed', 'void', 'refunded']);
    for (const o of orders) {
      const oid = String(o.id ?? '');
      if (!oid || ordersWithKot.has(oid) || String(o.code ?? '').startsWith('SAMPLE-')) continue;
      const items = orderLineMap.get(oid) ?? [];
      if (!items.length) continue;
      if (HIDE.has(String(o.orderStatus ?? '').toLowerCase())) continue;
      const tableId = o.tableId != null && String(o.tableId) !== '0' ? String(o.tableId) : undefined;
      list.push({
        id: `order-${oid}`,
        number: String(o.orderNumber || o.code || `Order #${oid}`),
        station: o.assignedStation ? String(o.assignedStation) : undefined,
        status: String(o.orderStatus ?? 'Placed'),
        tableId,
        tableLabel: (tableId && tableMap.get(tableId)) || (tableId ? `Table ${tableId}` : 'Unassigned'),
        issuedAt: o.placedAt && String(o.placedAt) !== '0001-01-01T00:00:00' ? String(o.placedAt) : (o.indate ? String(o.indate) : undefined),
        orderId: oid,
        items,
        isOrder: true,
      });
    }
    return list;
  }, [rows, orders, tableMap, itemMap, orderLineMap]);

  // Tickets the kitchen has finished (Ready/Served) leave the table lanes and
  // collect in the "Ready to Bill" lane so staff can see what to bill (#19).
  const billReady = useMemo(() => kots.filter((k) => READY_TO_BILL.has(k.status)), [kots]);

  const groups = useMemo<TableGroup[]>(() => {
    const m = new Map<string, TableGroup>();
    for (const k of kots) {
      if (READY_TO_BILL.has(k.status)) continue; // moved to the bill lane
      const key = k.tableId ?? k.tableLabel;
      let g = m.get(key);
      if (!g) { g = { key, label: k.tableLabel, kots: [], itemCount: 0 }; m.set(key, g); }
      g.kots.push(k);
      g.itemCount += k.items.reduce((s, it) => s + it.quantity, 0);
    }
    return [...m.values()].sort((a, b) => (a.label.startsWith('Unassigned') ? 1 : 0) - (b.label.startsWith('Unassigned') ? 1 : 0) || a.label.localeCompare(b.label));
  }, [kots]);

  async function updateStatus(k: Kot, value: string) {
    if (k.isOrder) {
      // Synthetic ticket (order has no KOT row yet) → write the stage to the
      // RestaurantOrder; its id is `order-<id>`.
      const oid = k.orderId ?? k.id.replace(/^order-/, '');
      setOrders((prev) => prev.map((o) => (String(o.id) === oid ? { ...o, orderStatus: value } : o)));
      try {
        // Spread the full row — the PUT is a full replace; a minimal body nulls
        // customerName/grandTotal/tableId/description.
        const raw = orders.find((o) => String(o.id) === oid) ?? {};
        await apiClient.put('/restaurant/RestaurantOrder', { ...raw, id: Number(oid), status: 0, orderStatus: value });
        toast.success('Order updated', `${k.number} → ${value}`);
      } catch (e) {
        toast.error('Update failed', e instanceof Error ? e.message : 'Try again');
        void refresh();
      }
      return;
    }
    setRows((prev) => prev.map((r) => (String(r.id) === k.id ? { ...r, kotStatus: value } : r)));
    try {
      const raw = rows.find((r) => String(r.id) === k.id) ?? {};
      await apiClient.put('/restaurant/Kot', { ...raw, id: Number(k.id), status: 0, kotStatus: value });
      toast.success('KOT updated', `${k.number} → ${value}`);
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Try again');
      void refresh();
    }
  }

  const toggle = (key: string) => setOpen((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const TicketCard = ({ k }: { k: Kot }) => (
    <div className="rounded-md border border-border bg-surface-card p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-content-primary">{k.number}</div>
          <div className="flex items-center gap-2 text-[11px] text-content-muted">
            {k.station && <span>{k.station}</span>}
            {k.issuedAt && <span className="inline-flex items-center gap-1"><Clock size={10} />{fmt(k.issuedAt)}</span>}
            {k.orderId && <span>· order #{k.orderId}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: kotColor(k.status).bg, color: kotColor(k.status).fg }}>{k.status}</span>
          {(() => {
            const opts = k.isOrder ? ORDER_TICKET_STATUSES : KOT_STATUSES;
            const val = (opts as readonly string[]).includes(k.status) ? k.status : opts[0];
            return (
              <select
                value={val}
                onChange={(e) => updateStatus(k, e.target.value)}
                className="h-8 rounded-md border border-border bg-surface-card px-2 text-xs text-content-primary outline-none focus:border-primary"
                title={k.isOrder ? 'Change order status' : 'Change KOT status'}
              >
                {opts.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            );
          })()}
        </div>
      </div>
      {k.items.length > 0 ? (
        <ul className="mt-2 space-y-1 text-[12px]">
          {k.items.map((it, i) => (
            <li key={i} className="flex items-baseline gap-2 border-t border-border/60 pt-1">
              <span className="inline-flex h-5 min-w-[24px] items-center justify-center rounded bg-content-primary/5 px-1 text-[11px] font-semibold">{it.quantity}×</span>
              <span className="flex-1">{it.name}{it.note && <span className="ml-1 italic text-content-muted">“{it.note}”</span>}</span>
              {it.status && <span className="text-[10px] text-content-muted">{it.status}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-2 rounded bg-surface-app px-2 py-1.5 text-[11px] italic text-content-muted">No item lines captured for this ticket.</div>
      )}
    </div>
  );

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface-card px-4 py-3">
        <ChefHat className="text-orange-500" size={22} />
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Kitchen Order Tickets (KOT)</h1>
          <p className="text-[11px] text-content-muted">{kots.length} ticket(s) · {groups.length} table(s)</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </header>

      {err && <Alert variant="danger" className="m-3">{err}</Alert>}

      <div className="flex-1 overflow-y-auto p-3">
        {loading && rows.length === 0 ? (
          <div className="grid place-items-center py-20 text-sm text-content-muted">Loading tickets…</div>
        ) : groups.length === 0 && billReady.length === 0 ? (
          <div className="grid place-items-center py-20 text-center text-sm text-content-muted">
            <UtensilsCrossed className="mb-2 text-content-muted" size={28} />
            No kitchen tickets yet. They appear when an order is sent to the kitchen.
          </div>
        ) : (
         <>
          {billReady.length > 0 && (
            <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50/60 p-3">
              <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-emerald-800">
                <UtensilsCrossed size={15} /> Ready to Bill · {billReady.length}
                <span className="text-[11px] font-normal text-emerald-700">— kitchen done; collect payment on the Floor / Orders screen</span>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {billReady.map((k) => (
                  <div key={k.id}>
                    <div className="mb-0.5 truncate text-[11px] font-medium text-content-muted">{k.tableLabel}</div>
                    <TicketCard k={k} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {groups.length > 0 && (
          <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {groups.map((g) => (
              <Card key={g.key} padded={false} className="flex flex-col overflow-hidden">
                <button onClick={() => toggle(g.key)} className="flex items-center gap-2 px-3 py-2.5 text-left hover:bg-surface-card-hover">
                  {open.has(g.key) ? <ChevronDown size={16} className="text-content-muted" /> : <ChevronRight size={16} className="text-content-muted" />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-semibold text-content-primary">{g.label}</div>
                    <div className="mt-0.5 text-[11px] text-content-muted">{g.kots.length} ticket(s) · {g.itemCount} item(s)</div>
                  </div>
                </button>
                {open.has(g.key) && (
                  <div className="space-y-2 border-t border-border bg-surface-app p-2">
                    {g.kots.map((k) => <TicketCard key={k.id} k={k} />)}
                  </div>
                )}
              </Card>
            ))}
          </div>
          )}
         </>
        )}
      </div>
    </div>
  );
}

function fmt(v?: string): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
